<script setup lang="ts">
// Vue
import { computed, ref, watch } from "vue";

// Dependencies
import { v4 as uuidv4 } from "uuid";

// Components
import InputLabel from "./InputLabel.vue";

const props = defineProps<{
  value: string;
  label?: string;
  placeholder?: string;
  color: string;
  autoComplete?: boolean;
  disabled?: boolean;
  errorMsg?: string;
  length?: number;
  textOnly?: boolean;
  enterFunction?: Function;
  size: string;
  required?: boolean;
}>();

const emits = defineEmits(["update:value", "onEnter"]);

const value = ref<string>(props.value);
const hasChanged = ref<boolean>(false);
const id = ref<string>(uuidv4());

// Keep the local v-model state in sync when the parent re-points the `value`
// prop (e.g. the form switches to a different record and the description
// changes). Without this watcher the textarea was stuck displaying whatever
// the prop happened to be at mount time.
watch(
  () => props.value,
  (next) => {
    value.value = next;
  }
);

const color = computed(() => {
  if (props.color === "primary") return "focus:border-app-blue-600";
  return undefined;
});

const size = computed(() => {
  if (props.size === "xl") return "h-72 w-96";
  else if (props.size === "lg") return "h-48 w-72";
  else if (props.size === "md") return "h-24 w-48";
  else if (props.size === "sm") return "h-12 w-24";
  else return props.size;
});

const inputChanged = () => {
  if (value.value.length > 0) hasChanged.value = true;
  emits("update:value", value.value);
};

const handleOnEnter = (evt: any) => {
  if (props.enterFunction === undefined) return;
  if (evt.keyCode === 13 && evt.shiftKey) return;
  emits("onEnter", value.value);
};
</script>

<template>
  <div class="flex flex-col">
    <InputLabel
      :id="id"
      :label="label"
      :required="required" />

    <textarea
      :id="id ? id : `input-${id}`"
      v-model="value"
      name=""
      :placeholder="placeholder"
      :disabled="disabled"
      class="focus-input rounded-8 t-small resize-none border border-slate-200 p-3"
      :class="[
        color,
        size,
        { 'border-app-red-500': errorMsg && !hasChanged },
        disabled ? 'text-type-muted cursor-not-allowed bg-slate-200' : 'bg-slate-0',
      ]"
      @input="inputChanged"
      @keyup.enter="handleOnEnter">
    </textarea>

    <span
      v-if="errorMsg && !hasChanged"
      class="text-app-red-700 text-xs"
      >{{ errorMsg }}</span
    >
  </div>
</template>
